import { useEffect, useRef, useState, useCallback } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { getInnovators, getRelationships, getRelationshipTypes } from "../../services/innovatorApi";
import "./map.css";

const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";

// Color mapping for relationship types
const RELATIONSHIP_COLORS = {
  COLLABORATES_WITH: "#6bff6b",
  SAME_TYPE: "#ffaa6b",
  SIMILAR_TECHNOLOGY: "#4a9eff",
  SIMILAR_APPLICATION: "#9b59b6",
  SEMANTICALLY_SIMILAR: "#e67e22",
  HIGHLY_SIMILAR: "#e74c3c",
  SHARES_TECHNOLOGY: "#3498db",
  SHARES_APPLICATION: "#8e44ad",
  SAME_LIFECYCLE_STAGE: "#f39c12",
  SAME_TARGET_MARKET: "#16a085",
  SAME_TRL_RANGE: "#27ae60",
  SAME_FUNDING_STAGE: "#c0392b",
  COMPLEMENTARY_TECH: "#d35400",
};

function getRelationshipColor(relType) {
  return RELATIONSHIP_COLORS[relType] || "#ffffff";
}

// Relationship type labels (matching MapControls)
const RELATIONSHIP_LABELS = {
  SIMILAR_TECHNOLOGY: "Similar Tech",
  SIMILAR_APPLICATION: "Similar Application",
  SEMANTICALLY_SIMILAR: "Semantic Similarity",
  HIGHLY_SIMILAR: "Highly Similar",
  SHARES_TECHNOLOGY: "Shares Technology",
  SHARES_APPLICATION: "Shares Application",
  SAME_LIFECYCLE_STAGE: "Same Lifecycle",
  SAME_TARGET_MARKET: "Same Market",
  SAME_TRL_RANGE: "Similar TRL",
  SAME_FUNDING_STAGE: "Same Funding Stage",
  COMPLEMENTARY_TECH: "Complementary Tech",
  COLLABORATES_WITH: "Collaborates",
  SAME_TYPE: "Same Type",
};

function escapeHtml(text) {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

function createPopupContent(properties) {
  const {
    company_name,
    type,
    contact_title,
    website,
    linkedin,
    description,
    location,
    tags,
  } = properties;

  let html = `
    <div class="popup-header">
      <h3>${escapeHtml(company_name || "Unknown Company")}</h3>
      ${type ? `<div class="company-type">${escapeHtml(type)}</div>` : ""}
    </div>
    <div class="popup-body">
  `;

  if (contact_title) {
    html += `
      <div class="popup-section">
        <h4>Contact</h4>
        <p>${escapeHtml(contact_title)}</p>
      </div>
    `;
  }

  if (description) {
    html += `
      <div class="popup-section">
        <h4>Description</h4>
        <p>${escapeHtml(description)}</p>
      </div>
    `;
  }

  if (location) {
    html += `
      <div class="popup-location">
        ${escapeHtml(location)}
      </div>
    `;
  }

  if (tags && Array.isArray(tags) && tags.length > 0) {
    html += `
      <div class="popup-section">
        <h4>Focus Areas</h4>
        <div class="popup-tags">
          ${tags.map((tag) => `<span class="popup-tag">${escapeHtml(tag)}</span>`).join("")}
        </div>
      </div>
    `;
  }

  const links = [];
  if (website) {
    links.push(
      `<a href="${escapeHtml(website)}" target="_blank" rel="noopener" class="popup-link">Website</a>`
    );
  }
  if (linkedin) {
    links.push(
      `<a href="${escapeHtml(linkedin)}" target="_blank" rel="noopener" class="popup-link">LinkedIn</a>`
    );
  }

  if (links.length > 0) {
    html += `
      <div class="popup-section">
        <div class="popup-links">
          ${links.join("")}
        </div>
      </div>
    `;
  }

  html += "</div>";
  return html;
}

const Map3D = ({ onFilterChange, selectedFilter }) => {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const allRelationshipsRef = useRef(null);
  const relationshipTypesRef = useRef([]);
  const dataLoadedRef = useRef(false); // Track if data has been loaded
  const notificationSentRef = useRef(false); // Track if we've notified parent about relationship types

  // Define helper functions first (before useEffect that uses them)
  const addInnovatorLayer = useCallback((geojsonData, relationshipData) => {
    if (!map.current) return;

    const sourceId = "innovators";
    const layerId = "innovators-points";

    // Add innovator data source
    map.current.addSource(sourceId, {
      type: "geojson",
      data: geojsonData,
    });

    // Add relationship arcs
    if (relationshipData && relationshipData.features && relationshipData.features.length > 0) {
      map.current.addSource("connection-arcs", {
        type: "geojson",
        data: relationshipData,
      });

      // Add connection arcs main layer
      try {
        const glowMatchCases = [];
        Object.entries(RELATIONSHIP_COLORS).forEach(([type, color]) => {
          const hexToRgba = (hex) => {
            const r = parseInt(hex.slice(1, 3), 16);
            const g = parseInt(hex.slice(3, 5), 16);
            const b = parseInt(hex.slice(5, 7), 16);
            return `rgba(${r}, ${g}, ${b}, 0.3)`;
          };
          glowMatchCases.push(type, hexToRgba(color));
        });
        // Don't push fallback here - it goes in the match expression

        map.current.addLayer({
          id: "connection-arcs",
          type: "line",
          source: "connection-arcs",
          layout: {
            "line-join": "round",
            "line-cap": "round",
          },
          paint: {
            "line-color": [
              "match",
              ["get", "relationship_type"],
              ...Object.entries(RELATIONSHIP_COLORS).flatMap(([type, color]) => [type, color]),
              "#ffffff",
            ],
            "line-width": [
              "case",
              ["has", "similarity_score"],
              [
                "interpolate",
                ["linear"],
                ["get", "similarity_score"],
                0.6,
                2,
                0.7,
                3,
                0.85,
                5,
                0.95,
                8,
              ],
              3,
            ],
            "line-opacity": [
              "case",
              ["has", "similarity_score"],
              [
                "interpolate",
                ["linear"],
                ["get", "similarity_score"],
                0.6,
                0.4,
                0.7,
                0.6,
                0.85,
                0.8,
                0.95,
                1.0,
              ],
              0.7,
            ],
            "line-blur": 0.5,
          },
        });

        // Add glow effect layer
        map.current.addLayer({
          id: "connection-arcs-glow",
          type: "line",
          source: "connection-arcs",
          layout: {
            "line-join": "round",
            "line-cap": "round",
          },
          paint: {
            "line-color": [
              "match",
              ["get", "relationship_type"],
              ...glowMatchCases,
              "rgba(255, 255, 255, 0.3)",
            ],
            "line-width": [
              "case",
              ["has", "similarity_score"],
              [
                "interpolate",
                ["linear"],
                ["get", "similarity_score"],
                0.6,
                4,
                0.75,
                6,
                0.85,
                10,
                0.95,
                15,
              ],
              5,
            ],
            "line-opacity": [
              "case",
              ["has", "similarity_score"],
              [
                "interpolate",
                ["linear"],
                ["get", "similarity_score"],
                0.6,
                0.2,
                0.75,
                0.4,
                0.85,
                0.6,
                0.95,
                0.8,
              ],
              0.4,
            ],
            "line-blur": [
              "case",
              ["has", "similarity_score"],
              [
                "interpolate",
                ["linear"],
                ["get", "similarity_score"],
                0.6,
                2,
                0.75,
                3,
                0.85,
                5,
                0.95,
                8,
              ],
              3,
            ],
          },
        }, "connection-arcs");

        // Add hover functionality for connection arcs
        // Change cursor on hover
        map.current.on("mouseenter", "connection-arcs", () => {
          if (map.current) {
            map.current.getCanvas().style.cursor = "pointer";
          }
        });

        map.current.on("mouseleave", "connection-arcs", () => {
          if (map.current) {
            map.current.getCanvas().style.cursor = "";
          }
        });

        // Highlight arc on hover by increasing width
        map.current.on("mouseenter", "connection-arcs", () => {
          if (map.current) {
            map.current.setPaintProperty("connection-arcs", "line-width", [
              "case",
              ["has", "similarity_score"],
              [
                "interpolate",
                ["linear"],
                ["get", "similarity_score"],
                0.6,
                4,
                0.7,
                5,
                0.85,
                7,
                0.95,
                12,
              ],
              5,
            ]);
          }
        });

        map.current.on("mouseleave", "connection-arcs", () => {
          if (map.current) {
            map.current.setPaintProperty("connection-arcs", "line-width", [
              "case",
              ["has", "similarity_score"],
              [
                "interpolate",
                ["linear"],
                ["get", "similarity_score"],
                0.6,
                2,
                0.7,
                3,
                0.85,
                5,
                0.95,
                8,
              ],
              3,
            ]);
          }
        });

        // Show popup on click with relationship info
        map.current.on("click", "connection-arcs", (e) => {
          const coordinates = e.lngLat;
          const properties = e.features[0].properties;
          const { source, target, relationship_type, similarity_score } = properties;

          const relTypeLabel = RELATIONSHIP_LABELS[relationship_type] || relationship_type;
          let popupContent = `
            <div class="relationship-popup">
              <h3>${escapeHtml(source)} ↔ ${escapeHtml(target)}</h3>
              <p><strong>Relationship:</strong> ${escapeHtml(relTypeLabel)}</p>
          `;

          if (similarity_score !== undefined) {
            popupContent += `<p><strong>Similarity:</strong> ${(similarity_score * 100).toFixed(1)}%</p>`;
          }

          popupContent += `</div>`;

          new mapboxgl.Popup()
            .setLngLat(coordinates)
            .setHTML(popupContent)
            .addTo(map.current);
        });
      } catch (error) {
        console.error("Error adding connection arcs:", error);
      }
    }

    // Add innovator points layer
    map.current.addLayer({
      id: layerId,
      type: "circle",
      source: sourceId,
      paint: {
        "circle-radius": [
          "interpolate",
          ["linear"],
          ["zoom"],
          4,
          5,
          8,
          7,
          12,
          10,
        ],
        "circle-color": "#4a9eff",
        "circle-stroke-width": 2,
        "circle-stroke-color": "#ffffff",
        "circle-opacity": 0.8,
      },
    });

    // Add popups on click
    map.current.on("click", layerId, (e) => {
      const coordinates = e.lngLat;
      const properties = e.features[0].properties;
      const popupContent = createPopupContent(properties);

      new mapboxgl.Popup()
        .setLngLat(coordinates)
        .setHTML(popupContent)
        .addTo(map.current);
    });

    // Change cursor on hover
    map.current.on("mouseenter", layerId, () => {
      if (map.current) {
        map.current.getCanvas().style.cursor = "pointer";
      }
    });

    map.current.on("mouseleave", layerId, () => {
      if (map.current) {
        map.current.getCanvas().style.cursor = "";
      }
    });
  }, []);

  const fitMapToBounds = useCallback((geojsonData) => {
    if (!map.current || !geojsonData.features || geojsonData.features.length === 0) return;

    const coordinates = geojsonData.features
      .map((feature) => feature.geometry.coordinates)
      .filter((coord) => coord && coord.length >= 2);

    if (coordinates.length === 0) return;

    const bounds = coordinates.reduce(
      (bounds, coord) => {
        return bounds.extend(coord);
      },
      new mapboxgl.LngLatBounds(coordinates[0], coordinates[0])
    );

    map.current.fitBounds(bounds, {
      padding: 50,
      maxZoom: 8,
    });
  }, []);

  const filterRelationships = useCallback((selectedType) => {
    if (!allRelationshipsRef.current || !map.current) return;

    let filteredFeatures;
    if (selectedType === "all" || !selectedType) {
      filteredFeatures = allRelationshipsRef.current.features;
    } else {
      filteredFeatures = allRelationshipsRef.current.features.filter(
        (feature) => feature.properties.relationship_type === selectedType
      );
    }

    const filteredData = {
      type: "FeatureCollection",
      features: filteredFeatures,
    };

    const source = map.current.getSource("connection-arcs");
    if (source) {
      source.setData(filteredData);
    }
  }, []);

  const loadInnovatorData = useCallback(async () => {
    try {
      setLoading(true);
      const [innovatorData, relationshipData, typesData] = await Promise.all([
        getInnovators(),
        getRelationships(),
        getRelationshipTypes(),
      ]);

      console.log("Loaded innovator data:", innovatorData);

      if (relationshipData && relationshipData.features) {
        console.log("Loaded relationship data:", relationshipData.features.length, "connections");
        allRelationshipsRef.current = relationshipData;
        // Add color properties to features
        relationshipData.features.forEach((feature) => {
          const relType = feature.properties.relationship_type;
          if (relType) {
            feature.properties.color = getRelationshipColor(relType);
          }
        });
      } else {
        console.log("No relationship data found");
        allRelationshipsRef.current = { type: "FeatureCollection", features: [] };
      }

      if (typesData && typesData.length > 0) {
        // Count relationships by type from actual relationship data
        const relationshipCounts = {};
        if (relationshipData && relationshipData.features) {
          relationshipData.features.forEach((feature) => {
            const relType = feature.properties?.relationship_type;
            if (relType) {
              relationshipCounts[relType] = (relationshipCounts[relType] || 0) + 1;
            }
          });
        }

        // Transform typesData to array of objects with type and count
        // typesData might be array of strings or array of objects
        const typesWithCounts = typesData.map((typeItem) => {
          const type = typeof typeItem === 'string' ? typeItem : typeItem.type;
          return {
            type: type,
            count: relationshipCounts[type] || 0,
          };
        });

        relationshipTypesRef.current = typesWithCounts;
        // Notify parent component about relationship types (only once)
        if (onFilterChange && typeof onFilterChange === 'function' && !notificationSentRef.current) {
          notificationSentRef.current = true;
          onFilterChange("relationshipTypes", typesWithCounts);
        }
      }

      addInnovatorLayer(innovatorData, allRelationshipsRef.current);
      setLoading(false);

      if (innovatorData.features && innovatorData.features.length > 0) {
        fitMapToBounds(innovatorData);
      }
    } catch (err) {
      console.error("Error loading data:", err);
      setError(`Failed to load data: ${err.message}`);
      setLoading(false);
    }
  }, [addInnovatorLayer, fitMapToBounds]); // Removed onFilterChange to prevent infinite loop

  // Now define useEffect hooks that use the functions above
  useEffect(() => {
    if (!mapContainer.current || dataLoadedRef.current) return; // Prevent multiple initializations

    if (MAPBOX_ACCESS_TOKEN === "YOUR_MAPBOX_ACCESS_TOKEN_HERE") {
      setError("Mapbox access token not configured");
      setLoading(false);
      return;
    }

    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/satellite-v9",
      center: [-98.5, 39.8],
      zoom: 1.5,
      projection: "globe",
    });

    map.current.addControl(new mapboxgl.NavigationControl(), "top-right");
    map.current.addControl(new mapboxgl.FullscreenControl(), "top-right");

    map.current.on("load", () => {
      // Configure globe atmosphere
      map.current.setFog({
        color: "rgb(186, 210, 235)",
        "high-color": "rgb(36, 92, 223)",
        "horizon-blend": 0.005,
        "space-color": "rgb(11, 11, 25)",
        "star-intensity": 0.8,
      });

      if (!dataLoadedRef.current) {
        dataLoadedRef.current = true;
        loadInnovatorData().catch((err) => {
          console.error("Failed to load innovator data:", err);
          dataLoadedRef.current = false; // Allow retry on error
        });
      }
    });

    map.current.on("error", (e) => {
      console.error("Map error:", e);
      if (e.error && e.error.message && !e.error.message.includes("WebGL")) {
        setError("Failed to load map. Please check your internet connection and Mapbox token.");
        setLoading(false);
      }
    });

    return () => {
      if (map.current) {
        map.current.remove();
        dataLoadedRef.current = false; // Reset on unmount
        notificationSentRef.current = false;
      }
    };
  }, []); // Empty dependency array - only initialize map once

  useEffect(() => {
    if (map.current && allRelationshipsRef.current && selectedFilter) {
      filterRelationships(selectedFilter);
    }
  }, [selectedFilter, filterRelationships]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <div ref={mapContainer} style={{ width: "100%", height: "100%" }} />
      {loading && (
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading innovators...</p>
        </div>
      )}
      {error && (
        <div className="error-message" style={{ color: "red", padding: "20px" }}>
          {error}
        </div>
      )}
    </div>
  );
};

export default Map3D;

